import type { AnchorHTMLAttributes, ReactNode } from 'react';
import RequestLink from 'Atoms/RequestLink/RequestLink';

/**
 * Cross-app destinations (the v2 app) are served as a separate SPA under `/v2/`.
 * A react-router `<Link>` only pushes client-side history, which this app's
 * router can't match, so the v2 app never loads. Detect those links so a real
 * anchor (full-page navigation) is rendered instead.
 */
export const isCrossAppLink = (link?: string): link is string =>
  !!link && link.startsWith('/v2');

/**
 * Navigates to a cross-app link. A full document load is unavoidable and
 * correct here — the target is a separate SPA — so this is a real page
 * navigation, co-located with `isCrossAppLink` so the render decision (which
 * element to use) and the navigation decision share one owner.
 */
export const navigateCrossApp = (link: string): void =>
  window.location.assign(link);

interface NotificationLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  to: string;
  children: ReactNode;
}

const NotificationLink = ({
  to,
  children,
  ...props
}: NotificationLinkProps) => (
  <RequestLink href={to} isJsonSchema={isCrossAppLink(to)} {...props}>
    {children}
  </RequestLink>
);

export default NotificationLink;
